import { Minus, Plus } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import type { AdjustDirection } from "../../types/cardWallets.types";
import {
  ADJUST_AMOUNT_DECIMALS,
  ADJUST_CURRENCY,
  ADJUST_REFERENCE_MAX_LENGTH,
  adjustAmountProblem,
  adjustReferenceProblem,
} from "../../utils/cardWalletAdjust";

export interface AdjustFormValues {
  external_reference: string;
  amount: string;
}

export interface AdjustBalanceFormProps {
  formId: string;
  direction: AdjustDirection;
  organizationName: string;
  /** Available balance as a decimal string — the debit cap. `null` while unknown. */
  balance: string | null;
  defaultValues: AdjustFormValues;
  onSubmit: (values: AdjustFormValues) => void;
  /** Fired on every edit, so the caller can mint a fresh idempotency key for what is now a different adjust. */
  onDirty: () => void;
}

/**
 * Step 1: the reference, then the amount.
 *
 * The reference goes FIRST because it is the thing being recorded and also the thing that decides whether the
 * movement is possible at all — a repeat answers 409 — so meeting that after typing an amount is strictly
 * worse. It is also the only free text the DTO accepts: there is no concept or note field, and the client
 * reads a fixed English `description`, so this is all a future reader will have.
 *
 * The amount never holds a sign. `ColocaAmountInput` strips anything outside `[\d.,]`, so a pasted `-500`
 * becomes `500`; the direction was committed to before this dialog opened and is applied at payload time.
 */
export function AdjustBalanceForm({
  formId,
  direction,
  organizationName,
  balance,
  defaultValues,
  onSubmit,
  onDirty,
}: AdjustBalanceFormProps) {
  const { t } = useTranslations("cardWallets");

  const schema = z
    .object({
      external_reference: z.string(),
      amount: z.string(),
    })
    .superRefine((values, ctx) => {
      const referenceProblem = adjustReferenceProblem(values.external_reference);
      if (referenceProblem) {
        ctx.addIssue({
          code: "custom",
          path: ["external_reference"],
          message: referenceProblem === "empty" ? t("validation_reference") : t("validation_reference_long"),
        });
      }

      // Mirrored in Zod as well as in the input, because a programmatic `setValue` bypasses the input's own
      // sanitation and Postgres would silently round a third decimal instead of refusing it.
      const amountProblem = adjustAmountProblem(values.amount, direction, balance);
      if (amountProblem) {
        const message =
          amountProblem === "precision"
            ? t("validation_amount_precision")
            : amountProblem === "over_balance"
              ? t("validation_amount_over_balance", {
                  balance: `${balance ?? ""} ${ADJUST_CURRENCY}`,
                  amount: `${values.amount} ${ADJUST_CURRENCY}`,
                })
              : t("validation_amount");
        ctx.addIssue({ code: "custom", path: ["amount"], message });
      }
    });

  const form = useZodForm(schema, defaultValues);
  const reference = form.watch("external_reference") ?? "";

  useEffect(() => {
    const subscription = form.watch(() => onDirty());
    return () => subscription.unsubscribe();
  }, [form, onDirty]);

  return (
    <ColocaForm form={form} id={formId} onSubmit={onSubmit} className="space-y-6">
      <ColocaAlert
        variant={direction === "credit" ? "secondary" : "warning"}
        icon={direction === "credit" ? Plus : Minus}
        text={t(direction === "credit" ? "mode_banner_credit" : "mode_banner_debit", {
          name: organizationName,
        })}
      />

      {/* `ColocaInputField` has a closed prop list and takes no `className`, so the monospace — which is what
          makes an operator-typed identifier readable — is applied to the input it renders. */}
      <div className="space-y-1 [&_input]:font-mono">
        <ColocaInputField
          name="external_reference"
          label={t("field_reference")}
          required
          maxLength={ADJUST_REFERENCE_MAX_LENGTH}
          placeholder={t("field_reference_placeholder")}
          autoComplete="off"
        />
        <div className="flex items-start justify-between gap-4">
          <p className="text-xs leading-relaxed text-muted-foreground">{t("field_reference_hint")}</p>
          <span className="shrink-0 font-mono text-xs text-muted-foreground">
            {t("field_reference_count", { used: reference.length })}
          </span>
        </div>
      </div>

      <ColocaAmountInput
        name="amount"
        label={t("field_amount")}
        description={t("field_amount_hint")}
        suffix={ADJUST_CURRENCY}
        maxFractionDigits={ADJUST_AMOUNT_DECIMALS}
        groupThousands
        placeholder="0,00"
      />
    </ColocaForm>
  );
}
